import React, { useRef, useEffect, KeyboardEvent, ChangeEvent, useCallback, useState } from 'react';
import { useDeviceDetect } from '../hooks/useDeviceDetect';

interface EditorProps {
  value: string;
  onChange: (value: string) => void;
  isSessionActive: boolean;
  wordCount: number;
  characterCount: number;
  onStartSession: (duration: number, isWordGoal: boolean, goalTarget?: number) => void;
  onResetSession: () => void;
  sessionDuration?: number;
  timeRemaining?: number;
  wordGoal?: number;
  isWordGoalMode?: boolean;
  fontFamily: string;
  fontSize: number;
}

// Declare the global window interface to include our custom property
declare global {
  interface Window {
    focusEditorTextArea?: () => void;
  }
}

const Editor: React.FC<EditorProps> = ({
  value,
  onChange,
  isSessionActive,
  wordCount,
  characterCount,
  onStartSession,
  onResetSession,
  sessionDuration = 0,
  timeRemaining = 0,
  wordGoal = 0,
  isWordGoalMode = false,
  fontFamily = 'Open Sans',
  fontSize = 16,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useDeviceDetect();
  const [shouldAddBottomPadding, setShouldAddBottomPadding] = useState(false);
  
  // Create a stable focusTextArea function with useCallback to prevent recreating it
  const focusTextArea = useCallback(() => {
    if (!textareaRef.current || !isSessionActive) return;
    
    // Make sure any active element is blurred first
    if (document.activeElement && document.activeElement !== textareaRef.current) {
      (document.activeElement as HTMLElement).blur();
    }
    
    // Focus the textarea directly
    textareaRef.current.focus();
    
    // Log to help with debugging
    console.log('Focus attempt on textarea');
  }, [isSessionActive]);

  // Check if user is near the bottom of the editor
  const checkScrollPosition = useCallback(() => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const scrollTop = textarea.scrollTop;
    const scrollHeight = textarea.scrollHeight;
    const clientHeight = textarea.clientHeight;
    
    // Calculate how close to the bottom the user is
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    const threshold = clientHeight * 0.2; // 20% of the visible area
    
    // Add padding when user is within 20% of the bottom
    setShouldAddBottomPadding(distanceFromBottom < threshold);
  }, []);
  
  // Focus textarea when session becomes active
  useEffect(() => {
    if (isSessionActive && textareaRef.current) {
      // Add a slight delay to ensure the DOM is ready
      // Use a longer delay on mobile to account for virtual keyboard and UI animations
      const focusDelay = isMobile ? 350 : 100;
      setTimeout(focusTextArea, focusDelay);
    }
  }, [isSessionActive, focusTextArea, isMobile]);
  
  // Create a global function to focus the textarea
  useEffect(() => {
    if (isSessionActive) {
      // Make the focus method available globally
      window.focusEditorTextArea = focusTextArea;
      
      return () => {
        delete window.focusEditorTextArea;
      };
    }
  }, [isSessionActive, focusTextArea]);
  
  // Set up a focus trap for the editor container
  useEffect(() => {
    if (!isSessionActive) return;
    
    const container = editorContainerRef.current;
    if (!container) return;
    
    // Capture all clicks inside the editor container
    const handleContainerClick = (e: MouseEvent) => {
      // Ensure the textarea has focus when clicking in the editor area
      if (textareaRef.current && document.activeElement !== textareaRef.current) {
        focusTextArea();
      }
    };
    
    // Capture focus leaving the editor
    const handleFocusOut = (e: FocusEvent) => {
      if (!isSessionActive) return;
      
      // Give a small timeout to see where focus went
      setTimeout(() => {
        if (document.activeElement?.closest('.allow-focus')) {
          return;
        }
        // If focus went outside our container, bring it back
        if (
          document.activeElement && 
          !container.contains(document.activeElement)
        ) {
          focusTextArea();
        }
      }, 10);
    };
    
    // Add the event listeners
    container.addEventListener('click', handleContainerClick);
    container.addEventListener('focusout', handleFocusOut);
    
    return () => {
      container.removeEventListener('click', handleContainerClick);
      container.removeEventListener('focusout', handleFocusOut);
    };
  }, [isSessionActive, focusTextArea]);
  
  // Adjust textarea height on mobile for better visibility
  useEffect(() => {
    if (textareaRef.current && isMobile) {
      textareaRef.current.style.minHeight = '50vh';
    }
  }, [isMobile]);

  // Set up scroll and input listeners to track position
  useEffect(() => {
    if (!textareaRef.current || !isSessionActive) return;
    
    const textarea = textareaRef.current;
    
    // Add event listeners for scroll, input, and cursor movement
    textarea.addEventListener('scroll', checkScrollPosition);
    textarea.addEventListener('input', checkScrollPosition);
    textarea.addEventListener('keyup', checkScrollPosition);
    textarea.addEventListener('click', checkScrollPosition);
    
    // Initial check
    checkScrollPosition();
    
    return () => {
      textarea.removeEventListener('scroll', checkScrollPosition);
      textarea.removeEventListener('input', checkScrollPosition);
      textarea.removeEventListener('keyup', checkScrollPosition);
      textarea.removeEventListener('click', checkScrollPosition);
    };
  }, [isSessionActive, checkScrollPosition]);
  
  // Handle text change
  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };
  
  // Handle keyboard shortcuts
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // No formatting shortcuts needed anymore
  };
  
  return (
    <div className="flex flex-col flex-1 editor-container">
      <div 
        className="flex-1 relative" 
        onClick={focusTextArea} 
        onTouchEnd={focusTextArea} 
        ref={editorContainerRef}
      >
        <textarea
          ref={textareaRef}
          className="w-full h-full p-4 resize-none bg-transparent text-primary-text dark:text-primary-text-dark focus:outline-none placeholder:text-black dark:placeholder:text-white"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={isSessionActive ? "Start writing here..." : "Session ended. Start a new session below."}
          disabled={!isSessionActive}
          onTouchEnd={focusTextArea}
          onBlur={(e) => {
            // When the textarea loses focus, bring it back if session is active
            if (isSessionActive) {
              if ((e.relatedTarget as HTMLElement)?.closest('.allow-focus')) {
                return;
              }
              setTimeout(focusTextArea, 10);
            }
          }}
          style={{ 
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            filter: 'none',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            fontFamily: fontFamily,
            fontSize: `${fontSize}px`,
            paddingBottom: shouldAddBottomPadding ? '20vh' : '1rem',
          }}
        />
      </div>
      
      {isMobile && (
        <div className="p-2 text-sm font-mono text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 flex justify-between word-count" style={{ backgroundColor: 'transparent', backdropFilter: 'none' }}>
          <span>Words: {wordCount}</span>
          <span>Chars: {characterCount}</span>
        </div>
      )}
    </div>
  );
};

export default Editor; 